/// <reference types="vite/client" />

import { Link } from 'react-router-dom'
import React, { type FormEvent } from 'react'
import { AuthShell } from './AuthShell'
import { apiPost } from '../../lib/api'
import { AUTH_FORGOT_PASSWORD } from '../../lib/endpoints'
import { colors } from '../../config/themeAuth'

type ApiResponse = {
  status: number
  message: string
}

export default function ForgotPassword() {
  const [email, setEmail] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [submitted, setSubmitted] = React.useState(false)

  const canSubmit = email.trim().length > 3

  async function sendForgotRequest() {
    await apiPost<ApiResponse, { email: string }>(
      AUTH_FORGOT_PASSWORD,
      { email: email.trim() }
    )
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await sendForgotRequest()
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send reset link')
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    setError(null)
    setLoading(true)
    try {
      await sendForgotRequest()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend reset link')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <AuthShell
        title="Check your inbox"
        subtitle={`We've sent a reset link to ${email}. It expires in 15 minutes.`}
      >
        <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full border" style={{ backgroundColor: colors.sand, borderColor: colors.border2 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: colors.accent }}>
            <path d="M22 2L11 13" />
            <path d="M22 2L15 22 11 13 2 9l20-7z" />
          </svg>
        </div>

        <button
          type="button"
          onClick={handleResend}
          disabled={loading}
          className="w-full rounded-2xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-60"
          style={{ backgroundColor: colors.ctaBg, color: colors.ctaColor }}
        >
          {loading ? 'Resending…' : 'Resend email'}
        </button>

        {error ? (
          <div className="mt-4 rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: colors.border, color: colors.darkOrange, backgroundColor: colors.sand }}>
            {error}
          </div>
        ) : null}

        <p className="mt-5 text-sm" style={{ color: colors.muted }}>
          Remembered it?{' '}
          <Link to="/signin" className="font-semibold underline" style={{ color: colors.ink }}>
            Back to sign in
          </Link>
        </p>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Forgot password?"
      subtitle="Enter your email and we'll send you a reset link right away."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            Email address
          </label>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="nudge-input mt-2 w-full rounded-2xl border px-4 py-3 text-sm"
            style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            required
          />
        </div>

        {error ? (
          <div className="rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: colors.border, color: colors.darkOrange, backgroundColor: colors.sand }}>
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="w-full rounded-2xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-60"
          style={{ backgroundColor: colors.ctaBg, color: colors.ctaColor }}
        >
          {loading ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <p className="mt-6 text-sm" style={{ color: colors.muted }}>
        Remembered it?{' '}
        <Link to="/signin" className="font-semibold underline" style={{ color: colors.ink }}>
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  )
}
